import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Chip,
  SvgIcon,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CategorySearch from './components/CategorySearch';
import CategoryList from './components/CategoryList';
import ProductCards from './components/ProductCards';
import ScraperStatus from './components/ScraperStatus';
import ScraperConfig from './components/ScraperConfig';
import ScraperLogs from './components/ScraperLogs';
import ScraperMonitor from './components/ScraperMonitor';
import SearchTermsPage from './components/SearchTermsPage';
import KeywordRankingReport from './components/KeywordRankingReport';
import SettingsPage from './components/SettingsPage';
import CategoryIcon from '@mui/icons-material/Category';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';
import ArticleIcon from '@mui/icons-material/Article';
import MonitorIcon from '@mui/icons-material/Monitor';
import SecurityIcon from '@mui/icons-material/Security';

const packageJson = require('../package.json');

// SVG Flag Icons using MUI SvgIcon for consistent alignment with other MUI icons
// Modern vibrant design - bright coral tones (no pure red)
const FlagUS = (props) => (
  <SvgIcon {...props} viewBox="0 0 60 42" sx={{
    width: 31,
    height: 22,
    borderRadius: '4px',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
    border: '1px solid rgba(255,255,255,0.3)',
  }}>
    <defs>
      <linearGradient id="usBlue" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#2E5984"/>
        <stop offset="100%" stopColor="#1A3A5C"/>
      </linearGradient>
    </defs>
    <rect width="60" height="42" fill="#E8563A"/>
    <g fill="#FFF">
      {[0, 6.46, 12.92, 19.38, 25.84, 32.3].map((y, i) => (
        <rect key={i} y={y} width="60" height="3.23"/>
      ))}
    </g>
    <rect width="24" height="22.68" fill="url(#usBlue)"/>
    <g fill="#FFF">
      {[2,8,14,20].map(x => [1.5,5,8.5,12,15.5,19].map(y => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1.2"/>
      )))}
      {[5,11,17].map(x => [3.25,6.75,10.25,13.75,17.25].map(y => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1.2"/>
      )))}
    </g>
  </SvgIcon>
);

const FlagUK = (props) => (
  <SvgIcon {...props} viewBox="0 0 60 42" sx={{
    width: 31,
    height: 22,
    borderRadius: '4px',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
    border: '1px solid rgba(255,255,255,0.3)',
  }}>
    <defs>
      <linearGradient id="ukBlue" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1E4785"/>
        <stop offset="100%" stopColor="#0A2351"/>
      </linearGradient>
    </defs>
    <rect width="60" height="42" fill="url(#ukBlue)"/>
    <path d="M0,0 L60,42 M60,0 L0,42" stroke="#FFF" strokeWidth="8"/>
    <path d="M0,0 L60,42 M60,0 L0,42" stroke="#E8563A" strokeWidth="4"/>
    <path d="M30,0 V42 M0,21 H60" stroke="#FFF" strokeWidth="12"/>
    <path d="M30,0 V42 M0,21 H60" stroke="#E8563A" strokeWidth="6"/>
  </SvgIcon>
);

const FlagJP = (props) => (
  <SvgIcon {...props} viewBox="0 0 60 42" sx={{
    width: 31,
    height: 22,
    borderRadius: '4px',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
    border: '1px solid rgba(200,200,200,0.5)',
  }}>
    <defs>
      <radialGradient id="jpSun" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FF6B4A"/>
        <stop offset="100%" stopColor="#E8563A"/>
      </radialGradient>
    </defs>
    <rect width="60" height="42" fill="#FFFFFF"/>
    <circle cx="30" cy="21" r="11" fill="url(#jpSun)"/>
  </SvgIcon>
);

const FlagCA = (props) => (
  <SvgIcon {...props} viewBox="0 0 60 42" sx={{
    width: 31,
    height: 22,
    borderRadius: '4px',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
    border: '1px solid rgba(200,200,200,0.5)',
  }}>
    <defs>
      <linearGradient id="caRed" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FF6B4A"/>
        <stop offset="100%" stopColor="#E8563A"/>
      </linearGradient>
    </defs>
    <rect width="60" height="42" fill="#FFFFFF"/>
    <rect width="15" height="42" fill="url(#caRed)"/>
    <rect x="45" width="15" height="42" fill="url(#caRed)"/>
    <polygon
      fill="url(#caRed)"
      points="30,12 31,15.2 33.6,14.6 33,17 37.8,16.3 37,18 39.5,20.4 38,21.3 38.6,23.6 35.4,22.9 35.8,24.7 32.6,23.7 30.9,31 29.1,31 27.4,23.7 24.2,24.7 24.6,22.9 21.4,23.6 22,21.3 20.5,20.4 23,18 22.2,16.3 27,17 26.4,14.6 29,15.2"
    />
  </SvgIcon>
);

// Market configuration for navigation
const MARKET_TABS = [
  { code: 'US', FlagIcon: FlagUS, label: 'US Best Sellers', route: '/best-sellers/us' },
  { code: 'UK', FlagIcon: FlagUK, label: 'UK Best Sellers', route: '/best-sellers/uk' },
  { code: 'JP', FlagIcon: FlagJP, label: 'JP Best Sellers', route: '/best-sellers/jp' },
  { code: 'CA', FlagIcon: FlagCA, label: 'CA Best Sellers', route: '/best-sellers/ca' },
];

// Tab order: one tab per MARKET_TABS entry, then KEYWORD, Config, Logs, Admin.
// Derived from MARKET_TABS so adding a market can't desync the indices.
const TAB_ROUTES = [
  ...MARKET_TABS.map(m => m.route),
  '/keyword-report',
  '/scraper-config',
  '/scraper-logs',
];
const ADMIN_TAB_INDEX = TAB_ROUTES.length;

// Wrapper component to pass market code from route params to ProductCards
function ProductCardsWrapper() {
  const { marketCode } = useParams();
  return <ProductCards marketCode={marketCode?.toUpperCase() || 'US'} />;
}

// Admin menu items configuration
const ADMIN_MENU_ITEMS = [
  { path: '/search-terms', label: 'Search Terms (By ASIN)', icon: TrendingUpIcon },
  { path: '/scraper-monitor', label: 'Scraper Monitor', icon: MonitorIcon },
  { path: '/categories', label: 'Category Search', icon: CategoryIcon },
  { path: '/settings', label: 'Settings', icon: SecurityIcon },
];

function AppContent() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [adminMenuAnchor, setAdminMenuAnchor] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isXs = useMediaQuery('(max-width:450px)');

  const handleCategoriesUpdate = (newCategories) => {
    setCategories(newCategories);
  };

  const isAdminPath = () => {
    const path = location.pathname;
    return ADMIN_MENU_ITEMS.some(item => path === item.path);
  };

  // Determine selected tab based on current path (indices come from TAB_ROUTES)
  const getTabValue = () => {
    const path = location.pathname;
    if (path === '/best-sellers') return 0;

    const marketIndex = MARKET_TABS.findIndex(m => path.startsWith(m.route));
    if (marketIndex !== -1) return marketIndex;

    const routeIndex = TAB_ROUTES.indexOf(path);
    if (routeIndex !== -1) return routeIndex;

    if (isAdminPath()) return ADMIN_TAB_INDEX;
    return 0;
  };

  const handleTabChange = (event, newValue) => {
    // The Admin tab opens a menu instead of navigating
    if (newValue === ADMIN_TAB_INDEX) {
      setAdminMenuAnchor(event.currentTarget);
      return;
    }
    navigate(TAB_ROUTES[newValue]);
  };

  const handleAdminMenuOpen = (event) => {
    setAdminMenuAnchor(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchor(null);
  };

  const handleAdminMenuItemClick = (path) => {
    navigate(path);
    handleAdminMenuClose();
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="fixed" sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(90deg, #FF9500 0%, #FF6B00 100%)',
          boxShadow: '0 4px 20px rgba(255, 107, 0, 0.4)',
        }}>
        <Toolbar sx={{ minHeight: { xs: '48px', sm: '56px', md: '64px' }, px: { xs: 0.5, sm: 1, md: 2 } }}>
          {/* Logo - hide on small screens */}
          {!isSmall && (
            <Box
              component="img"
              src="/capbarbell-logo.jpg"
              alt="Cap Barbell Logo"
              sx={{ height: { sm: 32, md: 40 }, mr: 1, borderRadius: 1 }}
            />
          )}
          {/* Title - hide on very small, shorter on small */}
          {!isXs && (
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '0.75rem', sm: '0.85rem', md: '1rem' },
                mr: 0.5,
                whiteSpace: 'nowrap'
              }}
            >
              {isSmall ? 'CAP' : 'Amazon Best Seller'}
            </Typography>
          )}
          <Chip
            label={`v${packageJson.version}`}
            size="small"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(4px)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.6rem',
              height: 18,
              border: '1px solid rgba(255,255,255,0.3)',
              mr: { xs: 0.5, sm: 1, md: 2 },
              display: { xs: 'none', sm: 'flex' }
            }}
          />
          <Tabs
            value={getTabValue()}
            onChange={handleTabChange}
            sx={{
              flexGrow: 1,
              minHeight: { xs: 48, sm: 56, md: 56 },
              '& .MuiTab-root': {
                minWidth: { xs: 36, sm: 48, md: 100 },
                minHeight: { xs: 48, sm: 56, md: 56 },
                px: { xs: 0.3, sm: 0.5, md: 2 },
                py: 0.5,
                transition: 'all 0.2s ease',
                // MUI dims unselected tabs by default, which on this coloured
                // AppBar reads as "disabled". Keep them fully opaque and only
                // step the contrast down, so the selected tab still stands out.
                color: 'rgba(255,255,255,0.82)',
                opacity: 1,
                '&:hover': {
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.12)',
                },
                '&.Mui-selected': {
                  color: '#fff',
                  fontWeight: 'bold',
                },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                backgroundColor: 'white',
              },
              '& .MuiTabs-scrollButtons': {
                width: { xs: 24, sm: 32 },
                color: 'white',
              }
            }}
            textColor="inherit"
            indicatorColor="secondary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {MARKET_TABS.map((market) => (
              <Tab
                key={market.code}
                icon={<market.FlagIcon />}
                label={market.code}
                aria-label={market.label}
                sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
              />
            ))}
            <Tab
              icon={<TrendingUpIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />}
              label={isMobile ? "KWD" : "KEYWORD"}
              aria-label="Keyword Ranking Report"
              sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
            />
            <Tab
              icon={<SettingsIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />}
              label={isMobile ? "CFG" : "CONFIG"}
              aria-label="Scraper Config"
              sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
            />
            <Tab
              icon={<ArticleIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />}
              label={isMobile ? "LOG" : "LOGS"}
              aria-label="Scraper Logs"
              sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
            />
            <Tab
              icon={<AdminPanelSettingsIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />}
              label={isMobile ? "ADM" : "ADMIN"}
              aria-label="Admin Menu"
              aria-haspopup="true"
              aria-expanded={Boolean(adminMenuAnchor)}
              onClick={handleAdminMenuOpen}
              sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
            />
          </Tabs>

          {/* Admin Dropdown Menu */}
          <Menu
            anchorEl={adminMenuAnchor}
            open={Boolean(adminMenuAnchor)}
            onClose={handleAdminMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                '& .MuiMenuItem-root': {
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 149, 0, 0.1)',
                  },
                },
              },
            }}
          >
            {ADMIN_MENU_ITEMS.map((item, index) => (
              <MenuItem
                key={item.path}
                onClick={() => handleAdminMenuItemClick(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 149, 0, 0.15)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 149, 0, 0.25)',
                    },
                  },
                }}
              >
                <ListItemIcon>
                  <item.icon sx={{ color: location.pathname === item.path ? '#FF9500' : 'inherit' }} />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
      </AppBar>

      <Toolbar sx={{ minHeight: { xs: '48px', sm: '56px', md: '64px' } }} />

      <Container maxWidth="xl" sx={{ mt: { xs: 1, md: 4 }, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
        <Routes>
          <Route path="/" element={<Navigate to="/best-sellers/us" replace />} />
          <Route path="/categories" element={
            <>
              <CategorySearch
                onCategoriesUpdate={handleCategoriesUpdate}
                setLoading={setLoading}
              />
              <Box sx={{ mt: 4 }}>
                <CategoryList
                  categories={categories}
                  loading={loading}
                />
              </Box>
            </>
          } />
          {/* Market-specific Best Sellers routes */}
          <Route path="/best-sellers" element={<Navigate to="/best-sellers/us" replace />} />
          <Route path="/best-sellers/:marketCode" element={<ProductCardsWrapper />} />
          <Route path="/keyword-report" element={<KeywordRankingReport />} />
          <Route path="/search-terms" element={<SearchTermsPage />} />
          <Route path="/scraper-config" element={<ScraperConfig />} />
          <Route path="/scraper-monitor" element={<ScraperMonitor />} />
          <Route path="/scraper-logs" element={<ScraperLogs />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
      </Container>

      <ScraperStatus
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
      />
    </Box>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
